import React, { useEffect, useRef, useState } from 'react'
import { Modal, Spin, Tooltip, Button, message } from 'antd'
import { getJobDetail, retryJob } from '../../services/api/apiJobs'
import { LoadingOutlined } from '@ant-design/icons'
import ReactDOM from 'react-dom'
import downloadFileFromData from "./downloadFileFromData"
import moment from "moment/moment"

const ExportJobModal = ({ jobId, number, onClose }) => {
    const [link, setLink] = useState(null)
    const [error, setError] = useState('')
    const [isRetry, setIsRetry] = useState(false)
    // Đếm lỗi bằng ref thay vì state: nếu để trong dependency array của useEffect thì mỗi lần
    // poll lỗi sẽ teardown + setup lại effect, kéo theo một fetch NGAY lập tức thay vì chờ 5s
    // -> dồn request đúng lúc exporter-service đang yếu.
    const errorCountRef = useRef(0)

    useEffect(() => {
        let intervalId

        const fetchDetailInfo = async () => {
            try {
                const { success, data, message: mess } = await getJobDetail(jobId)
                if (!success) throw new Error(mess)

                if (data.status === 'error') setIsRetry(true)

                if (data.link_download) {
                    setLink(data.link_download)
                    clearInterval(intervalId)
                } else {
                    // Poll lại được -> xoá cảnh báo lỗi để UI quay về trạng thái đang xử lý
                    errorCountRef.current = 0
                    setError('')
                }
            } catch (error) {
                errorCountRef.current += 1
                // Chỉ HIỆN cảnh báo sau 5 lỗi liên tiếp, KHÔNG dừng interval.
                // Dừng interval ở đây sẽ khiến modal chết vĩnh viễn: deps chỉ có [jobId] nên
                // không gì khởi động lại được vòng poll, kể cả nút Retry -> job đã xong ở server
                // mà user vẫn thấy "thất bại" rồi bấm Export lại, tạo job trùng.
                // Giữ nhịp 5s để vòng poll tự hồi phục khi exporter-service sống lại.
                if (errorCountRef.current >= 5) {
                    setError(error.message)
                }
            }
        }

        fetchDetailInfo()
        intervalId = setInterval(fetchDetailInfo, 5000)

        return () => clearInterval(intervalId)
    }, [jobId])

    useEffect(() => {
        if (link) {
            const a = document.createElement('a')
            a.href = link
            a.download = ''
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            onClose()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link])

    const retry = async (jobId) => {
        try {
            const { success, message: mess } = await retryJob(jobId)
            if (!success) {
                throw new Error(mess)
            }
            message.success(`Job đã được chạy lại.`)
        } catch (error) {
            message.error(error.message)
        }
    }

    const _handleRetry = (id) => {
        retry(id)
        setIsRetry(false)
        errorCountRef.current = 0
        setError('')
    }

    return (
        <div>
            {
                !!link ? (
                    <p>
                        File đã sẵn sàng. Bấm để tải xuống:{' '}
                        <a href={link} target="_blank" rel="noopener noreferrer" onClick={onClose}>
                            <strong>Download Job {number}</strong>
                        </a>
                    </p>
                ) : (
                    <React.Fragment>
                        <div className='mb-2'>
                            Quá trình export đang được xử lý, vui lòng bấm vào <a
                                href="#!"
                                id="job-exporter"
                                onClick={(e) => {
                                    e.preventDefault()
                                    window.open(`/jobs/${jobId}`, '_blank')
                                    onClose()
                                }}
                            >
                                <strong>Job {number}</strong>
                            </a> xem file kết quả.
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {
                                !!error ? (
                                    <span className='text-danger'>Lấy file download thất bại.</span>
                                ) : (
                                    <>
                                        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                                        <span>Đang xử lý lấy file download , vui lòng chờ...</span>
                                    </>
                                )
                            }
                        </div>

                        {
                            isRetry &&
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span className="text-danger">Job error!</span>
                                <Tooltip title='Thử lại'>
                                    <Button
                                        className='btnRetry p-0 mx-2 my-1' type={'link'}
                                        style={{ height: 'unset' }}
                                        onClick={() => _handleRetry(jobId)}
                                    >
                                        <i className="fa fa-retweet" style={{ fontSize: 20 }} />
                                    </Button>
                                </Tooltip>
                            </div>
                        }
                    </React.Fragment>
                )
            }

        </div>
    )
}

export const createJobExport = (data = {}) => {
    const { _id: jobId, number } = data

    const div = document.createElement('div')
    document.body.appendChild(div)

    const destroy = () => {
        ReactDOM.unmountComponentAtNode(div)
        div.remove()
    }

    // Giữ handle của chính modal này để chỉ đóng nó khi job xong.
    // Trước đây dùng Modal.destroyAll() — hàm đó hủy MỌI modal tạo bằng Modal.method()
    // trong toàn app, nên một job export xong sẽ đóng ké cả modal của nghiệp vụ khác
    // (ImportTrackingModal, showRequestPhotoModal) và các Job modal khác đang mở.
    let modalRef = null
    const closeSelf = () => {
        if (modalRef) modalRef.destroy()
    }

    modalRef = Modal.success({
        title: <span className="text-success fs-14">Tạo Job thành công</span>,
        content: <ExportJobModal jobId={jobId} number={number} onClose={closeSelf} />,
        okText: 'Đóng',
        onOk: destroy,
        afterClose: destroy,
    })
}

/**
 *
 * @param restApi       - API ready to call
 * @param filename      - Custom filename to save
 * @returns {Promise<void>}
 */
export const createJobExportFromApi = async (restApi, filename) => {
    const {data: res} = await restApi

    if (typeof res !== 'string') {
        const {success, message: mess, data} = res
        if (!success) {
            throw new Error(mess)
        }

        if (Object.keys(data).length > 0) {
            return createJobExport(data)
        }

        throw new Error('Unknown error')
    }

    filename = filename || `export-data-${moment().format('YYYY-MM-DD')}.csv`
    downloadFileFromData(res, {filename})
}
